import React, { useContext, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
} from 'react-router-dom';
import ProfilePage from '../../components/core/profile/ProfilePage.js';
import { Context } from '../../context.js';
import SideBar from '../../components/core/sidebar/sidebar.jsx';
import Header from '../../components/core/header/header.jsx';
import Flow from '../../assets/flow.json';
import './page-content.scss';
import Module from '../../components/course/Module.jsx';
import Overview from '../../components/course/Overview/overview.jsx';
import AllHandouts from '../../components/course/Overview/AllHandouts.js';
import BagOfTricksPage from '../../components/shared/bagOfTricksPage/bagOfTricksPage.jsx';
import Login from '../../components/core/login/login.jsx';
import SignUp from '../../components/core/login/SignUp.js';
import DetailPage from '../../components/admin-page/pages/AdminDetailPage.js';
import ForgotUsernameOrPassword from '../../components/core/login/ForgotUsernameOrPassword.js';
import { HeaderProvider } from '../../components/core/header/headerContext.js';
import ResetPassword from '../../components/core/login/ResetPassword.jsx';
import FascilitatorHome from '../../components/admin-page/pages/FascilitatorHome.js';
import Data from '../../components/admin-page/pages/Data.js';
import GroupInfo from '../../components/admin-page/pages/GroupInfo.js';
import CreateGroupPage from '../../components/admin-page/pages/CreateGroupPage.js';
import EditFacilitatorInfoPage from '../../components/admin-page/pages/EditFacilitatorInfoPage.js';
import EditGroupInfoPage from '../../components/admin-page/pages/EditGroupInfoPage.js';
import FascilitatorTraining from '../../components/admin-page/pages/FascilitatorTraining.js';
import { WelcomeVideoNoLoading } from '../../components/admin-page/pages/facilitator/pages/WelcomeVideo.js';
import EzParentIntroVideo from '../../components/admin-page/pages/facilitator/pages/EzParentIntroVideo.js';
import { isReadOnlyPreviewEnabled } from '../../utils/readOnlyPreview';
import {
  ROLE_PARENT,
  getStoredRole,
  isAdminRole,
  setStoredRole,
} from '../../utils/authRole';

const ParentPreviewLauncher = () => {
  const context = useContext(Context);

  useEffect(() => {
    context.enableReadOnlyPreview();
  }, []);

  return <Redirect to='/course/overview' />;
};

const FacilitatorAccessLogout = () => {
  const context = useContext(Context);

  useEffect(() => {
    context.logout();
  }, [context]);

  return <Redirect to='/login' />;
};

const ParentProgramShell = ({ previewOnly = false }) => {
  const context = useContext(Context);
  const previewEnabled =
    context.state.isReadOnlyPreview || isReadOnlyPreviewEnabled();

  if (previewOnly && !previewEnabled) {
    return <Redirect to='/facilitator/home' />;
  }

  return (
    <HeaderProvider>
      <div>
        <Header />
        <SideBar />
        <div className='course-material' id='course-material'>
          <Switch>
            <Route exact path={'/bag-of-tricks-page'}>
              <BagOfTricksPage />
            </Route>
            <Route path={'/course/overview/handout/:id'}>
              <AllHandouts />
            </Route>
            <Route path={'/profile'}>
              <ProfilePage />
            </Route>
            <Route path={'/course/overview'}>
              <Overview />
            </Route>
            <Route path={'/reset-password'}>
              <ResetPassword />
            </Route>
            {Flow.map((item, i) => {
              return (
                <Route key={i} path={item.url}>
                  {item.type === 'Module' ? (
                    <Module
                      lastData={Flow[i - 1]}
                      nextData={Flow[i + 1]}
                      parentRoute={item.url}
                      data={item}
                      modIndex={i - 1}
                    />
                  ) : (
                    <Overview />
                  )}
                </Route>
              );
            })}
            <Redirect from='/' to='/course/overview' />
          </Switch>
        </div>
      </div>
    </HeaderProvider>
  );
};

const Routes = () => {
  const context = useContext(Context);
  const storedRole = getStoredRole();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      context.logout();
      return;
    }

    if (isAdminRole(storedRole)) {
      context.login(token, false, { skipBootstrap: true });
      context.setIsAdmin(true, localStorage.getItem('program'));
      return;
    }

    setStoredRole(storedRole || ROLE_PARENT);
    context.setIsAdmin(false, null);

    context.getProgressData().then((result) => {
      if (!result) {
        context.logout();
        return;
      }

      const token = localStorage.getItem('token');
      context.login(token);

      context.setIsAdmin(false, null);
    });
  }, []);
  // if not authorized and it has completed checking if it should be authorized
  if (!context.state.isAuth.auth && !context.state.isAuth.loading) {
    return (
      <Router>
        <Switch>
          <Route path={['/sign-up', '/facilitator/sign-up']} exact>
            <SignUp />
          </Route>
          <Route path={['/login', '/facilitator/login']} exact>
            <Login />
          </Route>
          <Route path={'/facilitator-sign-up'} exact>
            <Redirect to='/facilitator/sign-up' />
          </Route>
          <Route path={'/facilitator-login'} exact>
            <Redirect to='/facilitator/login' />
          </Route>
          <Route path={'/reset-password'}>
            <ForgotUsernameOrPassword />
          </Route>
          <Route path={'/facilitator'}>
            <Redirect to='/facilitator/login' />
          </Route>
          <Redirect from={'/'} to={'/login'} />
        </Switch>
      </Router>
    );
  }

  if (context.state.isAuth.auth && context.state.isAdmin.isAdmin) {
    if (!isAdminRole(storedRole)) {
      return (
        <Router>
          <FacilitatorAccessLogout />
        </Router>
      );
    }

    // If the subdomain is 'facilitator', redirect to the admin page
    return (
      <Router>
        <Switch>
          <Route path='/facilitator/welcome-video' exact>
            <WelcomeVideoNoLoading />
          </Route>
          <Route path='/facilitator/ezparent-intro-video' exact>
            <EzParentIntroVideo />
          </Route>
          <Route path='/facilitator/dashboard' exact>
            <FascilitatorHome />
          </Route>
          <Route path='/facilitator/dashboard/create-group' exact>
            <CreateGroupPage />
          </Route>
          <Route path='/facilitator/dashboard/edit-facilitators' exact>
            <EditFacilitatorInfoPage />
          </Route>
          <Route path='/facilitator/dashboard/group/:groupId/edit' exact>
            <EditGroupInfoPage />
          </Route>
          <Route path='/facilitator/dashboard/group/:groupId'>
            <GroupInfo />
          </Route>
          <Route path='/facilitator/dashboard/data'>
            <Data />
          </Route>
          <Route path='/facilitator/parent-preview' exact>
            <ParentPreviewLauncher />
          </Route>
          <Route path='/bag-of-tricks-page'>
            <ParentProgramShell previewOnly />
          </Route>
          <Route path='/course'>
            <ParentProgramShell previewOnly />
          </Route>
          <Route path='/profile'>
            <ParentProgramShell previewOnly />
          </Route>
          <Route path='/reset-password'>
            <ParentProgramShell previewOnly />
          </Route>
          <Route path='/facilitator'>
            <FascilitatorTraining />
          </Route>
          {/* ...{fascRoutes} */}

          <Route path='/users/:id'>
            <DetailPage />
          </Route>

          <Redirect from='/' to='/facilitator/home' />
        </Switch>
      </Router>
    );
  }

  //logged In. Check if user is an admin and then to admin page if true. Go to normal user page if not

  if (context.state.isAuth.auth && !context.state.isAdmin.isAdmin) {
    return (
      <Router>
        <Switch>
          <Route path='/facilitator'>
            <FacilitatorAccessLogout />
          </Route>
          <Route path='/'>
            <ParentProgramShell />
          </Route>
        </Switch>
      </Router>
    );
  }
};

export default Routes;
